import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BotonGenerarDocumentoIndependiente from '../components/Documentos/BotonGenerarDocumentoIndependiente';
import TarjetaGenerarPacto from '../components/Documentos/TarjetaGenerarPacto';
import caratulaRegistry from '../components/Caratula/base/caratulaRegistry';
import { coincideBusqueda } from '../utils/textoComparable';
import PageHeader from '../components/ui/PageHeader';
import '../style/pages/HomePage.css';

// Un botón por cada tipo que el sistema sabe generar sin trámite. El ícono es solo estético:
// el endpoint sale del registro (`tipo`/`urlBase` en caratulaRegistry.js). Reajuste conserva
// el menú de variantes adentro de su propia tarjeta; Jubilación, Pensión y Reajuste de Pensión
// van desglosadas en una tarjeta por variante (mismo criterio que los 3 pactos) — para esos se
// fija `variante` y se reusan el título/detalle que ya tiene declarados caratulaRegistry.
const CARATULAS = [
    { key: 'RTI', tipo: 'RTI', icono: 'bi-file-earmark-text' },
    { key: 'ART', tipo: 'ART', icono: 'bi-heart-pulse' },
    { key: 'PNC', tipo: 'PNC', icono: 'bi-file-earmark-medical' },
    { key: 'AMPARO_PNC', tipo: 'AMPARO_PNC', icono: 'bi-shield-check' },
    { key: 'AMPARO_LEY_16986', tipo: 'AMPARO_LEY_16986', icono: 'bi-shield-check' },
    { key: 'PUAM', tipo: 'PUAM', icono: 'bi-person-badge' },
    { key: 'DECLARATORIA_HEREDEROS', tipo: 'DECLARATORIA_HEREDEROS', icono: 'bi-diagram-3' },
    { key: 'GANANCIAS', tipo: 'GANANCIAS', icono: 'bi-percent' },
    { key: 'PLAN_PAGO', tipo: 'PLAN_PAGO', icono: 'bi-credit-card' },
    { key: 'RECONOCIMIENTO', tipo: 'RECONOCIMIENTO', icono: 'bi-award-fill' },
    { key: 'REAJUSTE', tipo: 'REAJUSTE', icono: 'bi-graph-up-arrow' },
    {
        key: 'JUBILACION-LEY_24476', tipo: 'JUBILACION', variante: 'LEY_24476', icono: 'bi-folder',
        etiqueta: 'Jubilación 24476', descripcion: 'La simple, sin evaluación médica',
    },
    {
        key: 'JUBILACION-LEY_27675', tipo: 'JUBILACION', variante: 'LEY_27675', icono: 'bi-folder',
        etiqueta: 'Jubilación 27675', descripcion: 'Con patología, regularidad y junta médica',
    },
    {
        key: 'JUBILACION-CAJA_PROVINCIA', tipo: 'JUBILACION', variante: 'CAJA_PROVINCIA', icono: 'bi-folder',
        etiqueta: 'Jubilación Caja de la Provincia', descripcion: 'RECO, datos de CIDI, moratoria, banco y subsidio',
    },
    {
        key: 'JUBILACION-CUIDADO_HIJOS', tipo: 'JUBILACION', variante: 'CUIDADO_HIJOS', icono: 'bi-folder',
        etiqueta: 'Jubilación Cuidado de Hijos', descripcion: 'Ley por hijos: cantidad, AUH/CUD, actas e iniciar',
    },
    {
        key: 'PENSION-DIRECTA', tipo: 'PENSION', variante: 'DIRECTA', icono: 'bi-people',
        etiqueta: 'Pensión Directa', descripcion: 'Domicilio del titular y del causante',
    },
    {
        key: 'PENSION-DERIVADA', tipo: 'PENSION', variante: 'DERIVADA', icono: 'bi-people',
        etiqueta: 'Pensión Derivada', descripcion: 'Causante jubilado: suma N° de Beneficio',
    },
    {
        key: 'PENSION-HIJO_DISCAPACIDAD', tipo: 'PENSION', variante: 'HIJO_DISCAPACIDAD', icono: 'bi-people',
        etiqueta: 'Pensión Hijo con Discapacidad', descripcion: 'Suma N° de Beneficio; un solo renglón de domicilio',
    },
    {
        key: 'REAJUSTE_PENSION-BADARO', tipo: 'REAJUSTE_PENSION', variante: 'BADARO', icono: 'bi-arrow-repeat',
        etiqueta: 'Reajuste Pensión Badaro', descripcion: 'Conceptos del reclamo, Índice puro e IPC',
    },
    {
        key: 'REAJUSTE_PENSION-REPARACION_HISTORICA', tipo: 'REAJUSTE_PENSION', variante: 'REPARACION_HISTORICA', icono: 'bi-arrow-repeat',
        etiqueta: 'Reajuste Pensión Rep. Histórica', descripcion: 'Suma Ley; un solo renglón de monto',
    },
];

/**
 * Textos por los que se encuentra una tarjeta en el filtro: el nombre que muestra y el del
 * módulo en caratulaRegistry (así "jubilacion" trae las cuatro variantes de Jubilación).
 */
export function nombresDeCaratula({ tipo, etiqueta }) {
    return [etiqueta, caratulaRegistry[tipo]?.etiqueta];
}

/**
 * "Generar Documentos Automáticos": las carátulas y pactos que el sistema sabe emitir sin que
 * exista un trámite. Cada tarjeta abre su propio flujo (buscar un cliente del sistema u optar
 * por seguir sin él, y si el tipo tiene variantes, cuál emitir) antes de mostrar el documento —
 * en blanco si no hay cliente, prellenado con sus datos si lo hay.
 *
 * El trabajo real vive en BotonGenerarDocumentoIndependiente (carátulas, reusa
 * caratulaRegistry y los mismos ModalCaratulaXxx que las fichas de trámite) y
 * TarjetaGenerarPacto (los 3 pactos, que ya eran "por cliente" antes de esta página).
 */
function GenerarDocumentosPage() {
    const navigate = useNavigate();
    const [filtro, setFiltro] = useState('');

    // Sin tildes, sin mayúsculas y por palabras (coincideBusqueda): "declaratoria" encuentra
    // "Declaratoria de Herederos" y "pension rep" encuentra "Reajuste Pensión Rep. Histórica".
    const caratulasVisibles = useMemo(
        () => CARATULAS.filter((c) => coincideBusqueda(nombresDeCaratula(c), filtro)),
        [filtro]
    );

    return (
        <div className="homepage-container area-herramientas">
            <PageHeader
                variante="plano"
                icono="file-earmark-richtext"
                titulo="Generar Documentos Automáticos"
                descripcion="Carátulas y pactos del estudio, con o sin un cliente cargado."
            />

            <div className="alert alert-info d-flex align-items-start gap-2 rounded-4 shadow-sm mb-4">
                <i className="bi bi-info-circle-fill mt-1"></i>
                <div>
                    Estos son, por ahora, los únicos documentos que el sistema genera de forma
                    automática. Si necesitás otra carátula o pacto que no está en esta lista,
                    comunicate con el equipo de desarrollo o{' '}
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        onClick={() => navigate('/herramientas/feedback')}
                    >
                        subí un feedback
                    </button>.
                </div>
            </div>

            <div className="eventos-section">
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-file-earmark-text me-2"></i>
                        Carátulas
                    </h2>
                    <div className="input-group" style={{ maxWidth: 360 }}>
                        <span className="input-group-text bg-white">
                            <i className="bi bi-search"></i>
                        </span>
                        <input
                            type="search"
                            className="form-control"
                            placeholder="Buscar carátula por nombre"
                            aria-label="Buscar carátula por nombre"
                            value={filtro}
                            onChange={(e) => setFiltro(e.target.value)}
                        />
                    </div>
                </div>
                {caratulasVisibles.length === 0 && (
                    <div className="text-muted d-flex align-items-center gap-2 mb-3">
                        <i className="bi bi-search"></i>
                        <span>Ninguna carátula coincide con &quot;{filtro}&quot;.</span>
                        <button type="button" className="btn btn-link p-0" onClick={() => setFiltro('')}>
                            Ver todas
                        </button>
                    </div>
                )}
                <div className="accesos-rapidos-home accesos-rapidos-grid">
                    {caratulasVisibles.map(({ key, tipo, variante, etiqueta, descripcion, icono }) => (
                        <BotonGenerarDocumentoIndependiente
                            key={key}
                            tipo={tipo}
                            variante={variante}
                            etiqueta={etiqueta}
                            descripcion={descripcion}
                            icono={icono}
                        />
                    ))}
                </div>
            </div>

            <div className="eventos-section">
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-file-earmark-check me-2"></i>
                        Pactos
                    </h2>
                </div>
                <div className="accesos-rapidos-home accesos-rapidos-grid">
                    <TarjetaGenerarPacto tipo="SIN_PORCENTAJE" />
                    <TarjetaGenerarPacto tipo="CON_PORCENTAJE" />
                    <TarjetaGenerarPacto tipo="BADARO" />
                </div>
            </div>
        </div>
    );
}

export default GenerarDocumentosPage;
